import React, {
  useRef,
  useEffect,
  useState,
  useLayoutEffect,
} from "react";
import {
  min,
  max,
  scaleLinear,
  axisBottom,
  select,
  axisLeft,
  line,
  curveCatmullRom,
  extent,
  zoom,
  drag,
  pointer,
  bisectCenter,
  mean,
} from "d3";

import { useDetailsContext } from "../details";

import { 
  distanceInPreferredUnitV2,
  elevationInPreferredUnitV2,
 } from "../../../services";

import "./elevation-profiled3.css";
 
const margin = { top: 40, right: 40, bottom: 30, left: 50 };
const fillColor = "#e6e6e6";
const waypointCreationTolerance = 0.1;

//svgs are put into an array so that we can draw them with d3
const flagPaths = [
  {
    fill: "#039edb",
    stroke: "rgba(0,0,0,.4)",
    viewbox: "0 0 16 16",
    d: "M 1.875 0.9375 L 1.875 14.0625 C 1.875 14.582031 1.457031 15 0.9375 15 C 0.417969 15 0 14.582031 0 14.0625 L 0 0.9375 C 0 0.417969 0.417969 0 0.9375 0 C 1.457031 0 1.875 0.417969 1.875 0.9375 Z M 3.070312 9.808594 C 2.984375 9.835938 2.894531 9.859375 2.8125 9.886719 L 2.8125 0.394531 C 3.558594 0.1875 4.484375 0 5.390625 0 C 6.460938 0 7.390625 0.265625 8.191406 0.496094 L 8.230469 0.507812 C 9.0625 0.742188 9.765625 0.9375 10.546875 0.9375 C 11.332031 0.9375 12.097656 0.738281 12.6875 0.523438 C 12.976562 0.417969 13.210938 0.3125 13.371094 0.238281 C 13.449219 0.199219 13.511719 0.167969 13.550781 0.144531 C 13.574219 0.132812 13.585938 0.128906 13.59375 0.121094 L 13.597656 0.121094 C 13.890625 -0.0429688 14.246094 -0.0429688 14.535156 0.125 C 14.820312 0.296875 15 0.601562 15 0.9375 L 15 8.4375 C 15 8.792969 14.800781 9.117188 14.480469 9.273438 L 14.0625 8.4375 C 14.480469 9.273438 14.480469 9.273438 14.480469 9.273438 L 14.476562 9.273438 L 14.472656 9.277344 L 14.453125 9.289062 C 14.433594 9.300781 14.40625 9.3125 14.378906 9.324219 C 14.3125 9.355469 14.226562 9.394531 14.117188 9.441406 C 13.890625 9.539062 13.574219 9.664062 13.191406 9.789062 C 12.4375 10.046875 11.390625 10.3125 10.3125 10.3125 C 9.226562 10.3125 8.402344 10.039062 7.703125 9.804688 L 7.675781 9.796875 C 6.972656 9.5625 6.394531 9.375 5.625 9.375 C 4.820312 9.375 3.863281 9.578125 3.070312 9.808594 Z M 3.070312 9.808594",},
  {
    fill: "#000000",
    stroke: "none",
    d: "M 13.953125 0 C 13.769531 0 13.574219 0.0390625 13.386719 0.125 C 12.050781 0.734375 11.058594 0.941406 10.230469 0.941406 C 8.472656 0.941406 7.453125 0.0078125 5.425781 0.0078125 C 4.414062 0.0078125 3.144531 0.242188 1.40625 0.949219 L 1.40625 0.703125 C 1.40625 0.316406 1.089844 0 0.703125 0 C 0.316406 0 0 0.316406 0 0.703125 L 0 14.296875 C 0 14.6875 0.316406 15 0.703125 15 C 1.089844 15 1.40625 14.683594 1.40625 14.296875 L 1.40625 11.25 C 2.976562 10.550781 4.21875 10.316406 5.296875 10.316406 C 7.453125 10.316406 8.957031 11.25 11.117188 11.25 C 12.050781 11.25 13.109375 11.074219 14.371094 10.574219 C 14.769531 10.425781 15 10.089844 15 9.730469 L 15 0.898438 C 15 0.324219 14.511719 0 13.953125 0 Z M 13.59375 9.367188 C 12.707031 9.6875 11.890625 9.839844 11.117188 9.839844 C 10.210938 9.839844 9.40625 9.636719 8.554688 9.417969 C 7.617188 9.179688 6.554688 8.910156 5.296875 8.910156 C 4.058594 8.910156 2.78125 9.175781 1.40625 9.722656 L 1.40625 2.464844 L 1.9375 2.25 C 3.324219 1.6875 4.46875 1.414062 5.429688 1.414062 C 6.253906 1.414062 6.867188 1.613281 7.574219 1.839844 C 8.320312 2.078125 9.160156 2.347656 10.230469 2.347656 C 11.269531 2.347656 12.347656 2.101562 13.597656 1.570312 L 13.597656 9.367188 Z M 13.59375 9.367188 ",
  }
];

export default function ElevationProfileD3({ activityId, handleAddWaypoint, handleMoveWaypoint, }) {
  const [state, dispatch] = useDetailsContext();
  const [route, setRoute ] = useState([]);
  const { waypoints } = state;

  const maxDistance = useRef(10);
  const maxAlt = useRef(10); 
  const avgAlt = useRef(5);
  const minAlt = useRef(0);

  const [wptIndicators, setWptIndicators] = useState([]);
  const svgRef = useRef();
  const elevationGraphWrapper = useRef();

  const [dimensions, setDimensions] = useState([
    window.innerHeight,
    window.innerWidth,
  ]);

  const [width, setWidth] = useState(500);
  const [height, setHeight] = useState(100);
  const [smallFactorDevice, setSmallFactorDevice] = useState(true);
 
  const [currentZoomState, setCurrentZoomState] = useState();
  const [scaleX, setScaleX] = useState();
  const [showWaypointLabels, setShowWaypointLabels] = useState("hidden");

  const handleElevMarkerClick = (id) => {
    const marker = document.querySelectorAll(`[alt='${id.replace("_flag","")}'`);
    if (marker.length > 0) marker[0].click();

    const payload = {
      id: id.replace("_flag",""),
      lat: wptIndicators.filter((point) => point.waypointId === id.replace("_flag",""))[0]?.lat,
      lon: wptIndicators.filter((point) => point.waypointId === id.replace("_flag",""))[0]?.lon,
      elapsedTime: wptIndicators.filter((point) => point.waypointId === id.replace("_flag",""))[0]?.elapsedTime,
      description: wptIndicators.filter((point) => point.waypointId === id.replace("_flag",""))[0]?.description,
      title: wptIndicators.filter((point) => point.waypointId === id.replace("_flag",""))[0]?.title,
      type: 'table',
    };

    if (payload.lat && payload.lon) dispatch({ type: 'SET_ACTIVE_WAYPOINT', payload: payload });
  };

  useEffect(() => {
    // console.log("Elevation profile D3: Update dimensions");
    const { width, height } = elevationGraphWrapper.current.getBoundingClientRect();
    setWidth(width);
    setHeight(height);
  }, [elevationGraphWrapper]);

  useEffect(() => {
    if (dimensions.width < 600) setSmallFactorDevice(true);
    else setSmallFactorDevice(false);
  }, [dimensions]);

  const handleDisplayWptNames = (e) => {
     if (showWaypointLabels === "hidden") setShowWaypointLabels("visible");
     else setShowWaypointLabels("hidden");
  }

  useEffect(() => {
    // console.log("Elevation profile D3: Update local route with distance and elevation in preferred unit");
    const updateRoute = state.selectedRoute.route;
    updateRoute.forEach(function (d) { 
      d.distancePrefUnit = distanceInPreferredUnitV2(d.distance, state.userPrefUnits);
      d.elevationPrefUnit = elevationInPreferredUnitV2(d.elevation, state.userPrefUnits); 
    });
    setRoute(updateRoute);
    avgAlt.current = mean(state.selectedRoute.route, (d) => d.elevationPrefUnit);
    maxDistance.current = max(state.selectedRoute.route, (d) => d.distancePrefUnit);
    maxAlt.current = max(state.selectedRoute.route, (d) => d.elevationPrefUnit);
    minAlt.current = min(state.selectedRoute.route, (d) => d.elevationPrefUnit);
    // console.log(`maxDistance: ${maxDistance.current}, maxAlt: ${maxAlt.current}, minAlt: ${minAlt.current}, avgAlt: ${avgAlt.current}`);
    setCurrentZoomState();
    setScaleX();
  },[state.selectedRoute.route, state.userPrefUnits]);

  useLayoutEffect(() => {
    const updateDimensions = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener("resize", updateDimensions);
    updateDimensions();
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  //Execute on first render only
  useEffect(() => {
    const tooltipArea = select(svgRef.current).selectAll(".tooltipArea").attr("class", "tooltipArea"); 
    tooltipArea.style("opacity", 0);
  },[]);

  /* useEffect(() => {
    dispatch({ type: "SET_CURRENT_ZOOM", payload: currentZoomState });
  }, [currentZoomState, dispatch]); */

  useEffect(() => {
    // console.log(`Elevation profile D3: Update waypoints: total ${waypoints.length}`);
    if (waypoints.length > 0) {
      const foundPoints = waypoints.map((point) => {
        return {
          title: point.title,
          distance: route[point.index] ? route[point.index].distance : 0,
          elevation: route[point.index] ? route[point.index].elevation : 0,
          waypointId: "wpt" + point.id,
          distancePrefUnit: route[point.index] ? route[point.index].distancePrefUnit : 0,
          elevationPrefUnit: route[point.index] ? route[point.index].elevationPrefUnit : 0,
          description: point.description,
          elapsedTime: point.elapsedTime,
          lat: route[point.index] ? route[point.index].lat : 0,
          lon: route[point.index] ? route[point.index].lon : 0,
        };
      });
      setWptIndicators(foundPoints);
    }
  }, [route, waypoints, state.userPrefUnits, activityId]); //state.userPrefUnits needs to be here to render after unit change

  function addOrSelectWaypoint (routeIndex) {
    //TODO: This can be done better by looking at the svg instead so it works when zooming in
    const waypointDistancesPrefUnit = wptIndicators.map((wpt) => wpt.distancePrefUnit);
    const closestWpt = bisectCenter(waypointDistancesPrefUnit, route[routeIndex].distancePrefUnit);
    if ((Math.abs(route[routeIndex].distancePrefUnit - waypointDistancesPrefUnit[closestWpt]) > waypointCreationTolerance)) {
      return null;
    }
    else {
      // console.log("click too close to an existing waypoint, don't add");
      return closestWpt;
    }
  }
  
  function displayTooltip(event, d, xScale, yScale) {
    const tooltipArea = select(svgRef.current).selectAll(".tooltipArea").attr("class", "tooltipArea"); 
    const tooltipText = select(svgRef.current).selectAll(".tooltip-area__text").attr("class", "tooltip-area__text");
    const tooltipBckgrnd = select(svgRef.current).selectAll(".tooltipBackground");
    const index = bisectCenter(route.map((p) => p.distancePrefUnit), xScale.invert(pointer(event)[0]));

    const tooltipPosOffset = route[index].elevationPrefUnit > avgAlt.current ? 10 : -100;

    tooltipArea
      .style("opacity", 0.7)
      .attr("transform", `translate(${xScale(route[index].distancePrefUnit)},${yScale(route[index].elevationPrefUnit) + tooltipPosOffset})`);

    tooltipBckgrnd
      .style("opacity", 0.7);
    /*.append("line")
      .style("stroke", "lightgreen")
      .style("stroke-width", 10)
      .attr("x1", xScale(route[index].distancePrefUnit))
      .attr("x2", xScale(route[index].distancePrefUnit))
      .attr("y1", 0)
      .attr("y2", 200) */

    //TODO color changes with slope color: getSlopeColor(slope)
    tooltipText
      .text("")
      .append("tspan")
        .attr("x", 5)
        .attr("y", 15)
        .text (`Elevation: ${Math.round(route[index].elevationPrefUnit)}${state.userPrefUnits[2]} / ${route[index].slope ? Math.round(route[index].slope) : ""}%`)
      .append("tspan")
        .attr("x", 5)
        .attr("y", 30)
        .text(`Distance: ${Math.round(route[index].distancePrefUnit * 100) / 100}${state.userPrefUnits[1]}`)
      .append("tspan")
        .attr("x", 5)
        .attr("y", 45)
        .text(`Cumulated: ${Math.round(elevationInPreferredUnitV2(route[index].cumulatedAscent, state.userPrefUnits))}${state.userPrefUnits[2]}+ / ${Math.round(elevationInPreferredUnitV2(route[index].cumulatedDescent, state.userPrefUnits))}${state.userPrefUnits[2]}`);
    }

  function hideTooltip() {
    const tooltipArea = select(svgRef.current).selectAll(".tooltipArea").attr("class", "tooltipArea");
    tooltipArea
      .style("opacity", 0);
    }

  function zoomedOnChart(event, xScale) {
      // console.log("zooming"); 
      const zoomState = event.transform;
      setCurrentZoomState(zoomState);
      // const newXScale = zoomState.rescaleX(xScale);
      // console.log(`newScale: ${newXScale.domain()}, ${newXScale.range()}`);
      // xScale.domain(newXScale.domain());
      // setScaleX(newXScale);
      // console.log(`zoom - newScale: domain: ${newXScale.domain()}, range: ${newXScale.range()}`);
      // dispatch({type: "SET_CURRENT_ZOOM", payload: zoomState});
  }

  function waypointDragEnd(event, d, xScale, distancesPrefUnit, indexStart, wptIndicators, currentZoomState) {
    // const svg = select(svgRef.current);
    const wptSvgContent = select(svgRef.current).select(".waypointsSvgContent");

    wptSvgContent.selectAll("path#" + event.subject.waypointId + "_flag")
      .attr("stroke", null)
      .attr("d", flagPaths[0].d);

    // if (xScale) xScale.domain(scaleX.domain());
    const newX = event.x;
    // console.log(`currentZoomState (drag): ${xScale.domain()} drag ended at: ${newX}`);
    // const newX = event.x;
    const index = bisectCenter(distancesPrefUnit, xScale.invert(newX)); // ignoring currentZoomState, to be fixed
    // const index = bisectCenter(distancesPrefUnit, xScale.invert(newX));
    const pointId = event.subject.waypointId.replace("wpt","").replace("_flag","");

    try {
      const pointTitle = wptIndicators.filter((point) => point.waypointId === event.subject.waypointId)[0].title;
      const pointDistance = wptIndicators.filter((point) => point.waypointId === event.subject.waypointId)[0].distancePrefUnit;
      /* const pointTitle = event.subject.title; */
      const newPointDistance = xScale.invert(newX);

      // Add a check to only move if it's a move
      // console.log("dragging ended at:" + newX);

      if (newPointDistance === 0 || pointTitle === "Start") {
        // console.log("no move");
        return;
      }

      if (newPointDistance === max(wptIndicators.map((wpt) => wpt.distancePrefUnit))) {
        // console.log("no move");
        return;
      }

      if (indexStart === index || Math.round(newPointDistance * 1000) / 1000 === Math.round(route[index].distancePrefUnit * 1000) / 1000) {
        // console.log("no move");
        return;
      }

      // console.log("move to:" + newX);
      /* moveWaypoint(pointId, index, pointTitle)
      .then(() => getAllWaypoints(activityId))
      .then((res) => dispatch({ type: "FETCH_WAYPOINTS", payload: res })); */
      handleMoveWaypoint(pointId, index, pointTitle);
    } catch (e) {
      // console.log("couldn't complete move");
    }
  }


  useEffect(() => {
    // console.log("useEffect to update D3 graph");
    const svg = select(svgRef.current);
    const svgContent = svg.select(".chartSvgContent");
    const wptSvgContent = svg.select(".waypointsSvgContent");
  
    const { width, height } = elevationGraphWrapper.current.getBoundingClientRect();
    const distancesPrefUnit = route.map((p) => p.distancePrefUnit);

    const zoomState = currentZoomState;
    // Every scale has range and domain
    const xScale = scaleLinear()
    //  .range(xScaleGlobal.range())
      // .domain(scaleX ? scaleX.domain() : extent(route, (d) => d.distancePrefUnit))
      // .range(scaleX ? scaleX.range() : [margin.left, width - margin.top]);
      .range([margin.left, width - margin.top])
      .domain(extent(route, (d) => d.distancePrefUnit));

    // console.log(`currentZoomState: ${currentZoomState}`);
        
    if (zoomState) {
      const newXScale = zoomState.rescaleX(xScale);
      // console.log(`newScale: ${newXScale.domain()}, ${newXScale.range()}`);
      xScale.domain(newXScale.domain());
      setScaleX(newXScale);
    }

    /* if (scaleXdomain) {
      xScale.domain(scaleXdomain);
      console.log(`useEffect - newScale: ${xScale.domain()}, ${xScale.range()}`);

    } */
    
    // .range([height - margin.bottom, margin.top]);
    const yScale = scaleLinear()
      .range([height - margin.bottom, margin.top]) 
      .domain([
        min(route, (d) => d.elevationPrefUnit),
        max(route, (d) => d.elevationPrefUnit),
      ]);

    const elevationLine = line()
        .x((d) => xScale(d.distancePrefUnit))
        .y((d) => yScale(d.elevationPrefUnit))
        .curve(curveCatmullRom.alpha(1));
  
    const pathLine = (elevationLine && route) ? elevationLine(route) : null;

    //show indicator on map
    svg
      .on("mouseenter", (event, d) => {
        dispatch({ type: "SHOW_INDICATOR" });
      })
      .on("mousemove", (event, d) => {
        const index = bisectCenter(distancesPrefUnit, xScale.invert(pointer(event)[0]));
        if (index > -1) {
          displayTooltip(event, d, xScale, yScale);          
          dispatch({
                  type: "SET_INDICATOR",
                  payload: route[index],
                 });
        }
      })
      .on("mouseleave", (event, d) => {
        hideTooltip();
      }); 

    //render the elevation line
    svgContent
      .selectAll(".elevationProfileLine")
      .data([route.distancePrefUnit, route.elevationPrefUnit])
      .join("path")
      .attr("class", "elevationProfileLine")
      .attr("cursor", "pointer")
      .attr("d", pathLine)
      .attr('width', width)
      .attr('height', height)
      .on("click", (event, d) => {
        const index = bisectCenter(distancesPrefUnit, xScale.invert(pointer(event)[0]));
        // If the distance between points is too small, we should understand it as a select existing Waypoint
        const closestWpt = addOrSelectWaypoint(index);
        if (index && !closestWpt && !smallFactorDevice) handleAddWaypoint(activityId, index);
        else {
          //TODO: Find closest waypoint and trigger 
          handleElevMarkerClick(closestWpt.id);
          // console.log("skipping adding waypoint")
        };
      });
    
    // waypoint drag flag edition
    let indexStart = 0;
    const dragBehaviorFlag = drag()
      .on("start", (event, d) => {
        // console.log("started dragging at: " + event.x);
        indexStart = bisectCenter(distancesPrefUnit, xScale.invert(event.x));
      })
      .on("drag", (event, d) => {
        // use elevations from the route, not wild values from the dragging around
        // const newX = currentZoomState?.rescaleX(xScale)(event.x) ?? event.x;
        if (!smallFactorDevice) {
          const newX = event.x;
          wptSvgContent.selectAll("path#" + event.subject.waypointId + "_flag")
            .attr("transform", function(d){return 'translate('+ (newX) + ',' + (event.y - 15) + ')'})
            .attr("d", flagPaths[1].d);
          }
        // console.log("dragging" + event.x);
      })
      .on("end", (event, d) => {
       // console.log("dragging ended - currentZoomState: " + zoomState);
        waypointDragEnd(event, d, xScale, distancesPrefUnit, indexStart, wptIndicators, zoomState); 
      });

    wptSvgContent
      .selectAll("path")
      .attr('viewBox', "0 0 15 15")
      .data(wptIndicators)
      .join(
        enter => enter
          .append('path')
          .attr("d", flagPaths[0].d)
          .style("stroke", flagPaths[0].stroke)
          .style("fill", flagPaths[0].fill)
          .attr("class", ".waypointFlags")
          .attr('id', d => (d.waypointId + "_flag"))
          .attr('title', d => d.title)
          //moving the svgs depending on the data--you might have to play around with this depending on the pixel width of your svg
          .attr("transform", function(d){return 'translate('+ (xScale(d.distancePrefUnit)) + ',' + (yScale(d.elevationPrefUnit) - 15) + ')'})
          .call(dragBehaviorFlag)
          .on("mousemove", (event, d) => {
            // We changed from click to mousemove so it doesn't interfere with drag
            // console.log("click! on -> " + wptSvgContent.select("path#" + event.target.id))
            if (event.defaultPrevented) return; //dragged
            wptSvgContent.select("path#" + event.target.id).attr("d", flagPaths[1].d)
            
            handleElevMarkerClick(event.target.getAttribute('id'));
          })
          .on("mouseleave", (event, d) => {
            // console.log("mouseleave from flag");
            wptSvgContent.select("path#" + event.target.id).attr("d", flagPaths[0].d)
          }),
        update => update
          .attr("transform", function(d){return 'translate('+ (xScale(d.distancePrefUnit)) + ',' + (yScale(d.elevationPrefUnit) - 15) + ')'})
          .attr("fill", flagPaths[1].fill)
          .attr('id', d => (d.waypointId + "_flag")),
        exit => exit
          .remove(),
        );

    // add axes to each of the svg's
    const xAxis = axisBottom(xScale);
    const yAxis = axisLeft(yScale);

    svg
      .select(".x-axis")
      .attr('transform', `translate(0,${height - margin.bottom})`)
      .call(xAxis)
      .call(
        axisBottom(xScale)
          .ticks(8)
          .tickFormat((d) => `${d} ${state.userPrefUnits[1]}`)
      );

    svg
      .select(".y-axis")
      .attr('transform', `translate(${margin.left},0)`)
      .call(yAxis)
      .call(
        axisLeft(yScale)
          .ticks(4)
          .tickFormat((d) => `${d} ${state.userPrefUnits[2]}`)
      );
    
    wptSvgContent
      .select(".x-axis")
      .attr('transform', `translate(0,${height - margin.bottom})`)
      .call(xAxis);

    wptSvgContent
      .select(".y-axis")
      .attr('transform', `translate(${margin.left},0)`)
      .call(yAxis);  

    // zoom
    const zoomBehavior = zoom()
      .scaleExtent([1, 15])
      .translateExtent([
        [0, 0],
        [width, height],
        ])
      .on("zoom", (event, d) => {
        zoomedOnChart(event, xScale);
      });

    svg.call(zoomBehavior);
    wptSvgContent.call(zoomBehavior);

  }, [activityId, state.selectedRoute.route, state.userPrefUnits, dispatch, currentZoomState, wptIndicators, window.innerWidth, window.innerHeight]);

  // Add Waypoints labels
  useEffect(() => {
    const visibility = showWaypointLabels;
    // console.log("useEffect update D3 graph waypoint labels");
    const svg = select(svgRef.current);
    const wptSvgLabels = svg.select(".waypointsSvgLabels");

    // const { width, height } = elevationGraphWrapper.current.getBoundingClientRect();

    // Every scale has range and domain
    const xScale = scaleLinear()
      .range([margin.left, width - margin.top])
      .domain(extent(route, (d) => d.distancePrefUnit));
  
    if (currentZoomState) {
      const newXScale = currentZoomState.rescaleX(xScale);
      xScale.domain(newXScale.domain());
      } 
    /* if (scaleX) {
      xScale.domain(scaleX.domain());
      console.log(`useEffect - newScale (wptLabels): ${xScale.domain()}, ${xScale.range()}`);

    } */
    // .range([height - margin.bottom, margin.top]);
    const yScale = scaleLinear()
      .range([height - margin.bottom, margin.top]) 
      .domain([
        min(route, (d) => d.elevationPrefUnit),
        max(route, (d) => d.elevationPrefUnit),
      ]);

    wptSvgLabels
      .selectAll("text")
      .data(wptIndicators)
      .join(
        enter => enter
          .append("text")
          .text((d) => d.title)
          .attr("x", function(d) {return xScale(d.distancePrefUnit)})
          .attr("fill", "black")
          .style("visibility", visibility),

        update => update
          .attr("x", function(d) {return xScale(d.distancePrefUnit)})
          .text((d) => d.title)
          .attr("fill", "#004469")
          .style("visibility", visibility),
      )      
      .attr("y", function(d) {return (yScale(d.elevationPrefUnit) - 20)});
    
    // zoom
    const zoomBehavior = zoom()
    .scaleExtent([1, 15])
    .translateExtent([
      [0, 0],
      [width, height],
      ])
    .on("zoom", (event, d) => {
      zoomedOnChart(event, xScale);
    });

    wptSvgLabels.call(zoomBehavior);

  }, [route, currentZoomState, wptIndicators, showWaypointLabels, height, width]);

  return (
    <div id="elevationGraphWrapper" 
      ref={elevationGraphWrapper} 
      className="elevation-profile-containerd3">
      
      <svg ref={svgRef} 
        style={{width: "100%", 
        height: "100%",
        preserveAspectRatio:"none"}}>
      <defs>
        <clipPath id={activityId}>
          <rect x="0" y="0" width={width ? `${width}px` : "100%"} height={height ? `${height}px` : "100%"} />
        </clipPath>
      </defs>
      <g className="chartSvgContent" clipPath={`url(#${activityId})`}></g>
      <g className="waypointsSvgContent" clipPath={`url(#${activityId})`}></g>
      <g className="waypointsSvgLabels" clipPath={`url(#${activityId})`}></g>
      <g className="x-axis" style={{fontFamily: "Open sans", color: "#004469"}}/>
      <g className="y-axis" style={{fontFamily: "Open sans", color: "#004469"}}/>
      <g className="tooltipArea">
        <g className="backgroundTooltip">
          <rect x="0" y="0" width="200" height="50" rx="10" style={{fill: fillColor, strokeWidth: "1px", stroke: "black"}}/>
        </g>
        <text className="tooltip-area__text">Tooltip</text>
      </g>
      {!smallFactorDevice && <g className="labelsButton" onClick={e=>handleDisplayWptNames(e)}>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="rgb(230, 230, 230, 0.5)" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ai ai-ChatQuestion">
          {/* Copied from akar icons, <i class="ai-chat-question"></i> */}
          <path d="M14 19c3.771 0 5.657 0 6.828-1.172C22 16.657 22 14.771 22 11c0-3.771 0-5.657-1.172-6.828C19.657 3 17.771 3 14 3h-4C6.229 3 4.343 3 3.172 4.172 2 5.343 2 7.229 2 11c0 3.771 0 5.657 1.172 6.828.653.654 1.528.943 2.828 1.07"/>
          {showWaypointLabels === "hidden" && <path d="M10 8.484C10.5 7.494 11 7 12 7c1.246 0 2 .989 2 1.978s-.5 1.033-2 2.022v1m0 2.5v.5"/>}
          {showWaypointLabels === "visible" && <path d="M9 11l2.25 2L15 9"/>}
          <path d="M14 19c-1.236 0-2.598.5-3.841 1.145-1.998 1.037-2.997 1.556-3.489 1.225-.492-.33-.399-1.355-.212-3.404L6.5 17.5"/>
        </svg>
      </g> }
      </svg>
    </div>
  );
}
